import { useList } from '@refinedev/core';
import type { TfTrackRow } from '@verybigthings/trackframe-shared/schemas';
import { trackLabel } from '@verybigthings/trackframe-shared/tracks';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  Calculator,
  BarChart3,
  FileText,
  FileSignature,
  Shield,
  CheckCircle,
  Layers,
  AlertCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── per-track metadata ────────────────────────────────────────────────────

interface TrackMeta {
  icon: LucideIcon;
  features: { icon: LucideIcon; text: string }[];
}

function getTrackMeta(trackName: string): TrackMeta {
  const name = trackName.toLowerCase();

  if (name.includes('estimate')) {
    return {
      icon: Calculator,
      features: [
        { icon: BarChart3, text: 'Project estimates' },
        { icon: FileText, text: 'AI generation' },
        { icon: CheckCircle, text: 'Sales handoff' },
      ],
    };
  }

  if (name.includes('agreement') || name.includes('contract')) {
    return {
      icon: FileSignature,
      features: [
        { icon: FileText, text: 'Contract review' },
        { icon: Shield, text: 'Approvals & redlines' },
        { icon: CheckCircle, text: 'Compliance tracking' },
      ],
    };
  }

  // Generic fallback
  return {
    icon: Layers,
    features: [
      { icon: CheckCircle, text: 'AI-powered workflows' },
      { icon: BarChart3, text: 'Analytics & reports' },
      { icon: FileText, text: 'Document management' },
    ],
  };
}

// ─── feature list item ─────────────────────────────────────────────────────

function FeatureItem({
  Icon,
  text,
}: {
  Icon: React.ComponentType<{ className?: string }>;
  text: string;
}) {
  return (
    <div className="flex items-center gap-3 text-sm text-muted-foreground">
      <Icon className="w-4 h-4 text-primary shrink-0" />
      <span>{text}</span>
    </div>
  );
}

// ─── single track card ─────────────────────────────────────────────────────

interface TrackCardProps {
  track: TfTrackRow;
  animationDelay: number;
}

export function TrackCard({ track, animationDelay }: TrackCardProps) {
  const enabled = Boolean(track.baseUrl);
  const meta = getTrackMeta(track.name);
  const Icon = meta.icon;
  const Tag = enabled ? 'a' : 'div';

  return (
    <Tag
      key={track.id}
      {...(enabled ? { href: track.baseUrl as string } : {})}
      className={cn('group block', enabled ? 'cursor-pointer' : 'cursor-not-allowed')}
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <Card
        className={cn(
          'relative h-full border-2 transition-all duration-500 overflow-hidden',
          enabled
            ? [
                'hover:scale-[1.03] hover:shadow-2xl',
                'border-primary/20 hover:border-primary/60',
                'group-hover:shadow-primary/20',
              ]
            : ['border-muted opacity-60'],
        )}
      >
        {/* Decorative gradient overlay on hover */}
        {enabled && (
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary/5 to-primary/10 pointer-events-none" />
        )}

        {/* Top accent bar */}
        <div
          className={cn(
            'absolute top-0 left-0 right-0 h-1 bg-gradient-to-r transition-all duration-500',
            enabled
              ? [
                  'from-neutral-400 via-neutral-500 to-neutral-400',
                  'group-hover:from-primary group-hover:via-primary/80 group-hover:to-primary',
                ]
              : ['from-muted via-muted/80 to-muted'],
          )}
        />

        <CardHeader className="pb-6 pt-6 relative">
          {/* Icon tile row */}
          <div className="flex items-start justify-between mb-6">
            <div
              className={cn(
                'inline-flex items-center justify-center w-16 h-16 rounded-3xl relative transition-all duration-500',
                'bg-primary/10 border-2 border-primary/30',
                enabled && [
                  'group-hover:bg-primary',
                  'group-hover:scale-110 group-hover:shadow-2xl group-hover:text-white',
                ],
              )}
            >
              <Icon
                className={cn(
                  'w-7 h-7 relative z-10 text-primary transition-colors duration-500',
                  enabled && 'group-hover:text-white',
                )}
              />
              {/* Animated ping ring */}
              {enabled && (
                <div className="absolute -inset-0.5 rounded-3xl opacity-0 group-hover:opacity-40 animate-ping bg-primary/20 pointer-events-none" />
              )}
            </div>
          </div>

          {/* Title */}
          <CardTitle
            className={cn(
              'text-2xl mb-2 transition-transform duration-300 origin-left',
              enabled && 'group-hover:scale-105',
            )}
          >
            {trackLabel(track)}
          </CardTitle>

          {/* Description */}
          {track.description ? (
            <CardDescription className="text-sm leading-relaxed line-clamp-3">
              {track.description}
            </CardDescription>
          ) : (
            <CardDescription className="text-sm leading-relaxed text-muted-foreground/60 italic">
              {enabled ? 'Track application' : 'No frontend URL configured'}
            </CardDescription>
          )}
        </CardHeader>

        <CardContent className="pt-0 pb-6 flex flex-col flex-1">
          {/* Features list */}
          <div className="space-y-2 mb-6">
            {meta.features.map((feature, idx) => (
              <FeatureItem key={idx} Icon={feature.icon} text={feature.text} />
            ))}
          </div>

          {/* Launch / disabled CTA row */}
          <div
            className={cn(
              'mt-auto pt-4 px-4 pb-4 rounded-xl transition-all duration-300',
              'flex items-center justify-between',
              enabled
                ? [
                    'ring-2 ring-primary/20',
                    'group-hover:ring-0 group-hover:bg-primary',
                    'group-hover:text-white',
                  ]
                : ['bg-muted/40 ring-2 ring-muted/30 text-muted-foreground'],
            )}
          >
            <span className="font-semibold text-sm">
              {enabled ? 'Launch' : 'Unavailable'}
            </span>
            {enabled ? (
              <svg
                className="w-5 h-5 transition-transform group-hover:translate-x-2 duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            ) : (
              <AlertCircle className="w-4 h-4" />
            )}
          </div>
        </CardContent>
      </Card>
    </Tag>
  );
}

// ─── full-page wrapper states ──────────────────────────────────────────────

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-full bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 overflow-hidden">
      {/* Grid pattern decorative overlay with radial mask */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8882_1px,transparent_1px),linear-gradient(to_bottom,#8882_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_0%,#000_70%,transparent_110%)]" />
      </div>
      <div className="relative flex justify-center px-6 py-16">{children}</div>
    </div>
  );
}

// ─── hub home ─────────────────────────────────────────────────────────────

// Hub root page. Renders one Optima-style card per track the signed-in user
// can read. Cards link to `track.baseUrl`; tracks without one are rendered
// in a disabled state so admins can still see the gap.
export function TracksHome() {
  const { query } = useList<TfTrackRow>({
    resource: 'tracks',
    pagination: { mode: 'off' },
  });

  if (query.isLoading) {
    return (
      <PageWrapper>
        <div className="w-full max-w-7xl flex items-center justify-center py-24">
          <div className="text-center space-y-3">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">Loading tracks…</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  if (query.error) {
    const message = (query.error as { message?: string }).message ?? 'Unknown error';
    return (
      <PageWrapper>
        <div className="w-full max-w-7xl flex items-center justify-center py-24">
          <div className="bg-card rounded-2xl border border-destructive/30 p-8 max-w-md text-center shadow-lg">
            <AlertCircle className="w-10 h-10 text-destructive mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">Failed to load tracks</h2>
            <p className="text-sm text-muted-foreground">{message}</p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  const tracks: TfTrackRow[] = query.data?.data ?? [];

  if (tracks.length === 0) {
    return (
      <PageWrapper>
        <div className="w-full max-w-7xl flex items-center justify-center py-24">
          <div className="bg-card rounded-2xl border p-8 max-w-md text-center shadow-lg">
            <Layers className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-lg font-semibold mb-2">No tracks yet</h2>
            <p className="text-sm text-muted-foreground">
              Register your first track in the admin to see it here.
            </p>
          </div>
        </div>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <div className="w-full max-w-7xl">
        <div
          className={cn(
            'grid gap-8 mx-auto',
            tracks.length === 1
              ? 'grid-cols-1 max-w-sm'
              : tracks.length === 2
                ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl'
                : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl',
          )}
        >
          {tracks.map((track, index) => (
            <TrackCard key={track.id} track={track} animationDelay={index * 100} />
          ))}
        </div>
      </div>
    </PageWrapper>
  );
}
